import { Canvas } from '@react-three/fiber';
import { OrbitControls, ContactShadows, Html } from '@react-three/drei';

// Flange colour by drum material — matches the "factory 3d" look (metal = blue).
// The codebase had no plastic case, so we add one here.
const FLANGE_BY_TYPE = { metal: '#2F80ED', wood: '#8B6914', plastic: '#5AA469' };
const BARREL_BY_TYPE = { metal: '#4a6a94', wood: '#5C4020', plastic: '#3f7a5a' };

const num = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

const DrumViewer = ({
  drum,
  cableDiameterMm,
  fillFraction = 1,
  cableHex = '#B87333',
  height = 380
}) => {
  const hasDims =
    drum && num(drum.drumDiameter) > 0 && num(drum.barrelDiameter) > 0 && num(drum.flangeWidth) > 0;

  if (!hasDims) {
    return (
      <div
        style={{ height, background: '#eef2f6', borderRadius: 6 }}
        className="d-flex align-items-center justify-content-center text-700"
      >
        Enter drum dimensions to preview
      </div>
    );
  }

  const type = String(drum.drumType || '').toLowerCase();
  const isMetal = type === 'metal';
  const flangeColor = FLANGE_BY_TYPE[type] || '#8B6914';
  const barrelColor = BARREL_BY_TYPE[type] || '#5C4020';

  // mm → metres so the default lights/camera behave like the other 3D scenes
  const D = num(drum.drumDiameter) / 1000; // flange Ø
  const d = num(drum.barrelDiameter) / 1000; // barrel Ø
  const B = num(drum.flangeWidth) / 1000; // width
  const flangeR = D / 2;
  const barrelR = d / 2;
  const radialBuild = Math.max(0, flangeR - barrelR);

  // wound-cable outer radius reflects how full the drum is (0..1 of the radial build)
  const fill = Math.max(0, Math.min(1, num(fillFraction, 1)));
  const cableR = barrelR + radialBuild * fill * 0.98;
  const cableWidth = B * 0.96;
  const showCable = fill > 0.001;

  const flangeT = Math.max(0.02, D * 0.02);
  const camR = D * 1.7;

  return (
    <div style={{ height, background: '#eef2f6', borderRadius: 6, overflow: 'hidden' }}>
      <Canvas camera={{ position: [camR, D * 1.05, camR * 1.15], fov: 42 }} shadows dpr={[1, 2]}>
        <color attach="background" args={['#eef2f6']} />
        <ambientLight intensity={0.6} />
        <directionalLight position={[D * 3, D * 5, D * 2]} intensity={1.15} castShadow />
        <directionalLight position={[-D * 2, D * 2, -D * 3]} intensity={0.35} />
        <OrbitControls enablePan maxPolarAngle={Math.PI / 2.05} target={[0, flangeR, 0]} />

        {/* axis horizontal along Z, resting on the floor (same convention as factory 3d) */}
        <group position={[0, flangeR, 0]}>
          {/* barrel / core */}
          <mesh rotation={[Math.PI / 2, 0, 0]} castShadow receiveShadow>
            <cylinderGeometry args={[barrelR, barrelR, B, 40]} />
            <meshStandardMaterial color={barrelColor} roughness={0.85} metalness={isMetal ? 0.4 : 0.05} />
          </mesh>

          {/* wound cable */}
          {showCable && (
            <mesh rotation={[Math.PI / 2, 0, 0]} castShadow>
              <cylinderGeometry args={[cableR, cableR, cableWidth, 56]} />
              <meshStandardMaterial color={cableHex} roughness={0.9} />
            </mesh>
          )}

          {/* two flanges, coloured by material */}
          {[B / 2, -B / 2].map((z, i) => (
            <mesh key={i} position={[0, 0, z]} rotation={[Math.PI / 2, 0, 0]} castShadow receiveShadow>
              <cylinderGeometry args={[flangeR, flangeR, flangeT, 56]} />
              <meshStandardMaterial
                color={flangeColor}
                roughness={isMetal ? 0.35 : 0.7}
                metalness={isMetal ? 0.65 : 0.05}
              />
            </mesh>
          ))}

          <Html position={[0, flangeR + 0.18, 0]} center>
            <div
              style={{
                background: 'rgba(10,15,25,0.88)',
                color: '#fff',
                padding: '3px 8px',
                borderRadius: 4,
                fontSize: 11,
                fontFamily: 'monospace',
                whiteSpace: 'nowrap',
                pointerEvents: 'none'
              }}
            >
              {type || 'drum'} · D{drum.drumDiameter} / d{drum.barrelDiameter} · B{drum.flangeWidth}
              {num(cableDiameterMm) > 0 ? ` · cable Ø${num(cableDiameterMm).toFixed(2)}` : ''}
            </div>
          </Html>
        </group>

        <ContactShadows position={[0, 0, 0]} opacity={0.5} scale={D * 3} blur={2.2} far={D} />
      </Canvas>
    </div>
  );
};

export default DrumViewer;
